<template>
  <div class="py-5 pb-32 lg:px-5">
    <PageHeader class="container" title="Edit Booking" :back-step="true">
      <div slot="subtitle" class="flex space-x-2">
        <div class="py-1 font-medium leading-none" :class="parseBookingStateClass(form.status)">
          <fa :icon="['fas', 'clock']" size="xs" class="transform -translate-y-px" v-if="form.status === 'pending'" />
          <fa :icon="['fas', 'arrow-circle-right']" size="xs" class="transform -translate-y-px" v-if="form.status === 'approved'" />
          <fa :icon="['fas', 'scrubber']" size="xs" class="transform -translate-y-px" v-if="form.status === 'draft'" />
          <fa :icon="['fas', 'check-circle']" size="xs" class="transform -translate-y-px" v-if="form.status === 'complete'" />
          <fa :icon="['fas', 'times-circle']" size="xs" class="transform -translate-y-px" v-if="form.status === 'cancelled'" />
          {{ form.status | capitalize }}
        </div>
      </div>

      <div v-if="form.status === 'pending'" class="space-x-3">
        <button class="btn btn-secondary" v-submitting="submitting" @click="approveBooking()">
          <fa :icon="['far', 'check']" class="mr-2" /> Approve
        </button>
        <button class="btn btn-secondary" v-submitting="submitting" v-if="form.status === 'pending'" @click="cancelBooking()">
          <fa :icon="['far', 'ban']" class="mr-2" /> Cancel
        </button>
      </div>
      <div v-else-if="form.status === 'cancelled'">
        <button class="btn btn-secondary" v-submitting="submitting" @click="restoreBooking()">
          <fa :icon="['far', 'undo']" class="mr-2" /> Restore
        </button>
      </div>
    </PageHeader>
    
    <Loading v-if="loading" class="p-6" />
    <div class="container mt-6" v-else>
      <div class="grid space-y-4 lg:space-y-0 lg:grid-cols-3 lg:gap-6">
        <div class="space-y-6 lg:order-last lg:col-span-1">
          <!-- Date & Time -->
          <div>
            <h3 class="px-1 text-xl font-bold">
              Date &amp; Time
            </h3>
            <div class="mt-4 card">
              <div class="p-6">
                <fieldset class="form-group">
                  <label class="form-label">Date</label>
                  <DatePicker 
                    class="w-full"
                    color="indigo" 
                    mode="date" 
                    v-model="form.date">
                    <template v-slot="{ inputValue, inputEvents }">
                      <input type="text" class="form-input" :disabled="!editable" :value="inputValue" v-on="inputEvents">
                    </template>
                  </DatePicker>
                  <validations :errors="errors" field="date" />
                </fieldset>
                <fieldset class="form-group">
                  <label class="form-label">Time</label>
                  <input type="text" class="form-input" v-model="form.time" v-cleave="{ time: true, timePattern: ['h', 'm'], timeFormat: '24' }" :disabled="!editable">
                  <validations :errors="errors" field="date" />
                </fieldset>
                <button class="btn btn-primary" @click="saveBooking()" v-submitting="submitting" v-if="editable">
                  Save Changes
                </button>
              </div>
            </div>
          </div>

          <!-- Customer -->
          <div>
            <h3 class="px-1 text-xl font-bold">
              Customer
            </h3>
            <div class="mt-4 card">
              <div class="p-6 leading-loose">
                <div class="font-medium">
                  {{ form.customer_name }}
                </div>
                <div class="text-indgio-600">
                  <a :href="`mailto:${form.customer_email}`" target="_blank">{{ form.customer_email }}</a>  
                </div>
                <div class="text-gray-500">
                  {{ form.customer_phone }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-8 lg:col-span-2">
          <!-- Pickups -->
          <div>
            <h3 class="px-1 text-xl font-bold">
              {{ form.origins.length | pluralize('Pickup') }}
            </h3>
            <div v-if="form.origins && form.origins.length === 0" class="p-6 mt-4 text-gray-500 rounded-b-lg bg-gray-50">
              None
            </div>
            <div class="grid gap-4 mt-4" v-else>
              <div v-for="origin in form.origins" :key="origin.id" class="grid gap-4 p-6 card">

                <div class="items-start justify-between space-y-4 lg:space-y-0 lg:space-x-4 lg:flex">
                  <!-- Address -->
                  <div class="flex items-center justify-between flex-grow">
                    <div class="grid gap-3">
                      <div class="pb-3">
                        <div class="font-bold">
                          {{ origin.contact_name }}
                          <span class="font-normal" v-if="origin.name">
                            &mdash; {{ origin.name }}
                          </span>
                        </div>
                        <div class="text-sm text-gray-500">
                          {{ origin.formatted_address }}
                        </div>
                      </div>

                      <div v-if="origin.contact_phone">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'phone']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Contact Phone Number
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ origin.contact_phone }}
                        </div>
                      </div>

                      <div v-if="origin.contact_email">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'envelope']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Contact Email Address
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ origin.contact_email }}
                        </div>
                      </div>

                      <div v-if="origin.notes">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'sticky-note']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Booking Notes
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ origin.notes }}
                        </div>
                      </div>

                      <div v-if="origin.reference">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'hashtag']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Reference
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ origin.reference }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Edit Button -->
                  <div class="flex items-center justify-between space-x-4">
                    <div class="font-bold">
                      <currency :value="calculateAddressPrice(origin)" :currency-code="account.currency" :subunits-value="true" />
                    </div>
                    <router-link 
                      v-if="editable"
                      :to="{ 
                        name: 'BookingOriginEdit', 
                        params: { id: $route.params.id, origin_id: origin.id } 
                      }" 
                      class="btn btn-sm btn-outline">
                      Edit
                    </router-link>
                  </div>
                </div>

                <!-- Items -->
                <div>
                  <div class="flex justify-between py-3 border-b">
                    <div class="font-bold">
                      {{ origin.products.length ? origin.products.length : 'No' }} 
                      {{ origin.products.length | pluralize('Item') }}
                    </div>
                    <!-- <button class="text-sm font-medium text-indigo-500">
                      Edit Items
                    </button> -->
                  </div>
                  <div class="mt-2">
                    <div v-for="product in origin.products" :key="product.uuid" class="grid items-center justify-between grid-cols-2">
                      <div class="flex">
                        <div class="flex items-center justify-center flex-none w-8 h-8 mr-3 overflow-hidden transform translate-y-1 border rounded bg-gray-50">
                          <img v-if="product.image" :src="product.image" :alt="product.name" class="object-cover h-full">
                          <fa v-else :icon="['far', 'cube']" class="text-sm text-gray-300" />
                        </div>
                        <div>
                          <div class="flex items-center text-sm font-medium">
                            {{ product.name }} 
                          </div>
                          <div class="text-xs text-gray-500">
                            <currency :value="product.price" :currency-code="account.currency" :subunits-value="true" />
                          </div>
                        </div>
                      </div>
                      <div class="grid justify-between grid-cols-2 text-right">
                        <div class="mr-6 text-sm">
                          &times;{{ product.quantity }}
                        </div>
                        <currency class="text-sm font-medium" :value="product.subtotal" :currency-code="account.currency" :subunits-value="true" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Addons -->
                <div>
                  <div class="flex justify-between py-3 border-b">
                    <div class="font-bold">
                      {{ origin.addons.length ? origin.addons.length : 'No' }} 
                      {{ origin.addons.length | pluralize('Addon') }}
                    </div>
                    <!-- <button class="text-sm font-medium text-indigo-500">
                      Edit Addons
                    </button> -->
                  </div>
                  <div class="mt-2">
                    <div v-for="addon in origin.addons" :key="addon.uuid" class="flex items-center justify-between py-2">
                      <div class="flex">
                        <fa :icon="['fas', 'check']" class="mr-2 text-xs text-gray-400 transform translate-y-1" />
                        <div class="text-sm font-medium">
                          {{ addon.name }}
                        </div>
                      </div>
                      <div class="text-sm font-medium">
                        <currency :value="addon.price" :currency-code="account.currency" :subunits-value="true" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Adjustments -->
                <div>
                  <div class="flex justify-between py-3 border-b">
                    <div class="font-bold">
                      {{ origin.adjustments.length ? origin.adjustments.length : 'No' }} 
                      {{ origin.adjustments.length | pluralize('Adjustment') }}
                    </div>
                    <button class="text-sm font-medium text-indigo-500" @click="openAdjustmentForm('origin', origin.id)">
                      Add Adjustment
                    </button>
                  </div>
                  <div class="mt-2">
                    <div v-for="adjustment in origin.adjustments" :key="adjustment.id" class="flex items-center justify-between py-2">
                      <div class="flex">
                        <fa :icon="['fas', 'exchange']" class="mr-2 text-xs text-gray-400 transform translate-y-1" />
                        <div class="">
                          <div v-if="adjustment.note" class="text-sm">
                            {{ adjustment.note }}
                          </div>
                          <div v-else class="text-sm text-gray-500">
                            No Note
                          </div>
                          <div class="text-xs text-gray-500">
                            {{ adjustment.created_at | moment('DD MMM, YYYY, hh:mm A') }} by {{ adjustment.user.name }}
                          </div>
                        </div>
                      </div>
                      <div class="text-sm font-medium">
                        <currency :value="adjustment.amount" :currency-code="account.currency" :subunits-value="true" />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- Deliveries -->
          <div>
            <h3 class="px-1 text-xl font-bold">
              {{ form.destinations.length | pluralize(['Delivery', 'Deliveries']) }}
            </h3>
            <div v-if="form.destinations && form.destinations.length === 0" class="p-6 mt-6 text-gray-500 rounded-b-lg bg-gray-50">
              None
            </div>
            <div class="grid gap-4 mt-4" v-else>
              <div v-for="destination in form.destinations" :key="destination.id" class="grid gap-4 p-6 card">

                <div class="items-start justify-between space-y-4 lg:space-y-0 lg:space-x-4 lg:flex">
                  <!-- Address -->
                  <div class="flex items-center justify-between flex-grow">
                    <div class="grid gap-3">
                      <div class="pb-3">
                        <div class="font-bold">
                          {{ destination.contact_name }}
                          <span class="font-normal" v-if="destination.name">
                            &mdash; {{ destination.name }}
                          </span>
                        </div>
                        <div class="text-sm text-gray-500">
                          {{ destination.formatted_address }}
                        </div>
                      </div>

                      <div v-if="destination.contact_phone">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'phone']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Contact Phone Number
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ destination.contact_phone }}
                        </div>
                      </div>

                      <div v-if="destination.contact_email">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'envelope']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Contact Email Address
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ destination.contact_email }}
                        </div>
                      </div>

                      <div v-if="destination.notes">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'sticky-note']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Booking Notes
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ destination.notes }}
                        </div>
                      </div>

                      <div v-if="destination.reference">
                        <div class="flex items-center text-xs text-gray-500">
                          <fa :icon="['fas', 'hashtag']" 
                            class="flex-none mr-2 text-gray-400" 
                            fixed-width />
                          Reference
                        </div>
                        <div class="ml-6 -mt-1 text-sm">
                          {{ destination.reference }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Edit Button -->
                  <div class="flex items-center justify-between space-x-4">
                    <div class="font-bold">
                      <currency :value="calculateAddressPrice(destination)" :currency-code="account.currency" :subunits-value="true" />
                    </div>
                    <router-link 
                      v-if="editable"
                      :to="{ 
                        name: 'BookingDestinationEdit', 
                        params: { id: $route.params.id, destination_id: destination.id } 
                      }" 
                      class="btn btn-sm btn-outline">
                      Edit
                    </router-link>
                  </div>
                </div>

                <!-- Items -->
                <div>
                  <div class="flex justify-between py-3 border-b">
                    <div class="font-bold">
                      {{ destination.products.length ? destination.products.length : 'No' }} 
                      {{ destination.products.length | pluralize('Item') }}
                    </div>
                    <!-- <button class="text-sm font-medium text-indigo-500">
                      Edit Items
                    </button> -->
                  </div>
                  <div class="mt-2">
                    <div v-for="product in destination.products" :key="product.uuid" class="grid items-center justify-between grid-cols-2">
                      <div class="flex">
                        <div class="flex items-center justify-center flex-none w-8 h-8 mr-3 overflow-hidden transform translate-y-1 border rounded bg-gray-50">
                          <img v-if="product.image" :src="product.image" :alt="product.name" class="object-cover h-full">
                          <fa v-else :icon="['far', 'cube']" class="text-sm text-gray-300" />
                        </div>
                        <div>
                          <div class="flex items-center text-sm font-medium">
                            {{ product.name }} 
                          </div>
                          <div class="text-xs text-gray-500">
                            <currency :value="product.price" :currency-code="account.currency" :subunits-value="true" />
                          </div>
                        </div>
                      </div>
                      <div class="grid justify-between grid-cols-2 text-right">
                        <div class="mr-6 text-sm">
                          &times;{{ product.quantity }}
                        </div>
                        <currency class="text-sm font-medium" :value="product.subtotal" :currency-code="account.currency" :subunits-value="true" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Addons -->
                <div>
                  <div class="flex justify-between py-3 border-b">
                    <div class="font-bold">
                      {{ destination.addons.length ? destination.addons.length : 'No' }} 
                      {{ destination.addons.length | pluralize('Addon') }}
                    </div>
                    <!-- <button class="text-sm font-medium text-indigo-500">
                      Edit Addons
                    </button> -->
                  </div>
                  <div class="mt-2">
                    <div v-for="addon in destination.addons" :key="addon.uuid" class="flex items-center justify-between py-2">
                      <div class="flex">
                        <fa :icon="['fas', 'check']" class="mr-2 text-xs text-gray-400 transform translate-y-1" />
                        <div class="text-sm font-medium">
                          {{ addon.name }}
                        </div>
                      </div>
                      <div class="text-sm font-medium">
                        <currency :value="addon.price" :currency-code="account.currency" :subunits-value="true" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Adjustments -->
                <div>
                  <div class="flex justify-between py-3 border-b">
                    <div class="font-bold">
                      {{ destination.adjustments.length ? destination.adjustments.length : 'No' }} 
                      {{ destination.adjustments.length | pluralize('Adjustment') }}
                    </div>
                    <button class="text-sm font-medium text-indigo-500" @click="openAdjustmentForm('destination', destination.id)">
                      Add Adjustment
                    </button>
                  </div>
                  <div class="mt-2">
                    <div v-for="adjustment in destination.adjustments" :key="adjustment.id" class="flex items-center justify-between py-2">
                      <div class="flex">
                        <fa :icon="['fas', 'exchange']" class="mr-2 text-xs text-gray-400 transform translate-y-1" />
                        <div class="">
                          <div v-if="adjustment.note" class="text-sm">
                            {{ adjustment.note }}
                          </div>
                          <div v-else class="text-sm text-gray-500">
                            No Note
                          </div>
                          <div class="text-xs text-gray-500">
                            {{ adjustment.created_at | moment('DD MMM, YYYY, hh:mm A') }} by {{ adjustment.user.name }}
                          </div>
                        </div>
                      </div>
                      <div class="text-sm font-medium">
                        <currency :value="adjustment.amount" :currency-code="account.currency" :subunits-value="true" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal title="Add A Cost Adjustment" :open.sync="showCostAdjustmentModal">
        <div class="p-6">
          <BookingAdjustmentForm 
            :parent-type="adjustmentParentType"
            :parent-id="adjustmentParentId"
            @success="onAdjustmentAdded" />
        </div>
      </Modal>
      
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import BookingAdjustmentForm from './BookingAdjustmentForm.vue'

export default {
  name: 'EditBooking',

  metaInfo: {
    title: 'Edit Booking'
  },

  components: {
    DatePicker,
    BookingAdjustmentForm
  },

  async created () {
    await this.getBooking()
  },

  computed: {
    ...mapGetters(['account']),
    
    editAddressOpen () {
      return this.editAddress !== null
    },

    editable () {
      return this.form.status !== 'approved' && this.form.status !== 'complete'
    }
  },

  data () {
    return {
      loading: true,
      submitting: false,
      editAddress: null,
      errors: [],
      form: {},
      showCostAdjustmentModal: false,
      adjustmentParentType: null,
      adjustmentParentId: null
    }
  },

  methods: {
    async getBooking () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/bookings/${this.$route.params.id}`)
        this.form = data.booking

        if (this.form.date) {
          this.form.date = new Date(this.form.date)
        }
        
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    async saveBooking () {
      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/bookings/${this.$route.params.id}`, this.form)
        this.$router.push({ name: 'BookingList' })
      } catch (error) {
        console.error(error)
        this.errors = error.response.data.errors ? error.response.data.errors : []
      } finally {
        this.submitting = false
      }
    },

    async approveBooking () {
      try {
        this.submitting = true
        const { data } = await this.$api.post(`account/${this.$route.params.account}/approved-bookings/${this.$route.params.id}`)
        this.form = data.booking
      } catch (error) {
        console.error(error)
        this.errors = error.response.data.errors ? error.response.data.errors : []
      } finally {
        this.submitting = false
      }
    },

    async cancelBooking () {
      if (window.confirm('Are you sure you want to cancel this booking?')) {
        try {
          this.submitting = true
          await this.$api.post(`account/${this.$route.params.account}/cancelled-bookings/${this.$route.params.id}`)
          this.$router.push({ name: 'BookingList' })
          this.$toasted.show('Booking cancelled.')
        } catch (error) {
          console.error(error)
        } finally {
          this.submitting = false
        }
      }
    },

    async restoreBooking () {
      if (window.confirm('Are you sure you want to restore this booking?')) {
        try {
          this.submitting = true
          const { data } = await this.$api.delete(`account/${this.$route.params.account}/cancelled-bookings/${this.$route.params.id}`)
          this.form = data.booking
          this.$toasted.show('Booking restored.')
        } catch (error) {
          console.error(error)
        } finally {
          this.submitting = false
        }
      }
    },
    
    updateAddress (address) {
      this.form.origins.forEach((origin, index) => {
        if (origin.id === address.id) {
          this.form.origins[index] = address
        }
      })

      this.form.destinations.forEach((destination, index) => {
        if (destination.id === address.id) {
          this.form.destinations[index] = address
        }
      })

      this.editAddress = null
    },

    parseBookingStateClass (status) {
      if (status === 'pending') { return 'text-gray-500' }
      else if (status === 'draft') { return 'text-yellow-500' }
      else if (status === 'approved') { return 'text-blue-500' }
      else if (status === 'cancelled') { return 'text-red-500' }
      else if (status === 'complete') { return 'text-emerald-500' }
    },

    calculateAddressPrice (address) {
      const productPrice = address.products.reduce((accumulator, product) => {
        return accumulator + (product.price * product.quantity) 
      }, 0)
      const addonPrice = address.addons.reduce((accumulator, addon) => {
        return accumulator + addon.price
      }, 0)
      const adjustmentAmount = address.adjustments.reduce((accumulator, adjustment) => {
        return accumulator + adjustment.amount
      }, 0)
      return productPrice + addonPrice + adjustmentAmount
    },

    openAdjustmentForm (type, id) {
      this.adjustmentParentType = type
      this.adjustmentParentId = id
      this.showCostAdjustmentModal = true
    },

    onAdjustmentAdded () {
      this.showCostAdjustmentModal = false 
      this.$toasted.show('Adjustment added.')
      this.getBooking()
    }
  }
}
</script>